import * as Yup from 'yup'
import 'yup-phone-lite'
import {
  isPhoneNumberValid,
  parseCurrencyToNumber,
  parsePhoneNumber,
} from '../helpers'
import { ProductType } from '../types/types'

export const UserFormSchema = Yup.object().shape({
  firstname: Yup.string().required('First name is required'),
  lastname: Yup.string().required('Last name is required'),
  email: Yup.string().required('Email is required'),
  password: Yup.string().required('Password is required'),
})

export const CreateCategorySchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  description: Yup.string().required('Required'),
  comboTagLine: Yup.string().required('Required'),
  recommendedTagLine: Yup.string().required('Required'),
})

export const CreateSubCategorySchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  categoryId: Yup.string().required('Required'),
  isActive: Yup.boolean().required('Required'),
})

export const addLocationManualSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  city: Yup.string().required('Required'),
  state: Yup.string().required('Required'),
  long: Yup.number().required('Required'),
  lat: Yup.number().required('Required'),
})

export const addPickUpLocationManualSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  address: Yup.string().required('Required'),
})

export const addFulfilmentCenterSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  address: Yup.string().required('Required'),
  long: Yup.number().required('Required'),
  lat: Yup.number().required('Required'),
})

export const editFulfilmentCenterSchema = Yup.object().shape({
  address: Yup.string().required('Required'),
  long: Yup.number().required('Required'),
  lat: Yup.number().required('Required'),
})

export const editFulfilmentCenterDetailsSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
})

export const CreateTagSchema = Yup.object().shape({
  name: Yup.string().required('Tag name is required'),
  description: Yup.string().required('Tag description is required'),
})

export const AddComboItemSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  price: Yup.string()
    .test('is-valid-amount', `Invalid Amount`, function (value) {
      return parseCurrencyToNumber(value) !== null
    })
    .required('Price is Required'),
  minimumQuantity: Yup.number().min(0).required('Minimum quantity is required'),
  maximumQuantity: Yup.number().min(0).required('Maximum quantity is required'),
  required: Yup.boolean().required('Required'),
})

export const AddComboSectionSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string().required('Description is required'),
  minimumQuantity: Yup.number().min(0).required('Minimum quantity is required'),
  maximumQuantity: Yup.number().min(0).required('Maximum quantity is required'),
  required: Yup.boolean().required('Required'),
})

export const AddDeliveryLocationSchema = Yup.object().shape({
  locationId: Yup.string().required('Required'),
  minimumDeliveryTime: Yup.number().min(0).required('Required'),
  maximumDeliveryTime: Yup.number().min(0).required('Required'),
  type: Yup.string().required('Required'),
  deliveryTime: Yup.string().required('Required'),
})

export const SelectLocationSchema = Yup.object().shape({
  locationId: Yup.string().required('Required'),
})

export const SelectFulfilmentCenterSchema = Yup.object().shape({
  center: Yup.object().required('Required'),
})

export const AddProductVariantSchema = Yup.object().shape({
  variant: Yup.object().required('Variant is required'),
  values: Yup.array()
    .of(
      Yup.object()
        .shape({
          value: Yup.string().required('Value is required'),
          price: Yup.string()
            .test('is-valid-amount', `Invalid Amount`, function (value) {
              return parseCurrencyToNumber(value) !== null
            })
            .required('Price is required'),
          images: Yup.array().of(Yup.string().required('Required')).min(0),
          markupValue: Yup.number()
            .min(0, 'Minimum allowed value is 0')
            .max(50, 'Maximum allowed value is 50'),
        })
        .required('Required'),
    )
    .min(1),
})

export const SendVouchersSchema = Yup.object().shape({
  value: Yup.string()
    .test('is-valid-amount', `Invalid Amount`, function (value) {
      return parseCurrencyToNumber(value) !== null
    })
    .required('Required'),
  emails: Yup.array()
    .of(Yup.string().email('Invalid email').required('Required'))
    .min(1),
})

export const ManageReferralsSchema = Yup.object().shape({
  enableReferrals: Yup.boolean().required(),
  value: Yup.string()
    .test('is-valid-amount', `Invalid Amount`, function (value) {
      return parseCurrencyToNumber(value) !== null
    })
    .when('enableReferrals', (enableReferrals, schema) => {
      return enableReferrals[0] ? schema.required() : schema.optional()
    }),
  type: Yup.string().when('enableReferrals', (enableReferrals, schema) => {
    return enableReferrals[0] ? schema.required() : schema.optional()
  }),
})

export const EditVariationCombinationSchema = Yup.object().shape({
  price: Yup.string()
    .test('is-valid-amount', `Invalid Amount`, function (value) {
      return parseCurrencyToNumber(value) !== null
    })
    .required('Required'),
  inventoryCount: Yup.number().min(0).required('Required'),
  isActive: Yup.boolean().required('Required'),
  images: Yup.array().of(Yup.string().required('Required')).min(0),
})

export const UpdateProductSchema = Yup.object().shape({
  reason: Yup.object({
    value: Yup.string().required(),
    name: Yup.string().required(),
  }).required('Reason is required'),
  quantity: Yup.number().min(0).required('Quantity is required'),
})

export const UpdateProductPriceSchema = Yup.object().shape({
  reason: Yup.object({
    value: Yup.string().required(),
    name: Yup.string().required(),
  }).required('Reason is required'),
  price: Yup.string()
    .test('is-valid-amount', `Invalid Amount`, function (value) {
      return parseCurrencyToNumber(value) !== null
    })
    .required('Required'),
})

export const CreateVariantSchema = Yup.object().shape({
  name: Yup.string().required('Category name is required'),
})

export const AddSubVariantLabelSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
})

export const BulkUploadProductsSchema = Yup.object().shape({
  vendor: Yup.object({
    _id: Yup.string().required(),
    name: Yup.string().required(),
  }).required('Vendor is required'),
  file: Yup.array().min(1, 'Upload file').required('Upload file'),
})

export const CreateProductSchema = (productType: ProductType) => {
  return Yup.object().shape({
    name: Yup.string().required('Product name is required'),
    description: Yup.string().required('Product description is required'),
    images: Yup.array()
      .of(Yup.string())
      .min(1, 'Upload at least one image for this product'),
    price: Yup.string()
      .test('is-valid-amount', `Invalid Amount`, function (value) {
        return parseCurrencyToNumber(value) !== null
      })
      .required('Price is required'),
    quantityInStock: Yup.string(),
    vendor: Yup.object({
      _id: Yup.string().required(),
      name: Yup.string().required('Select a creator'),
    }).required('Vendor is required'),
    tags: Yup.array().of(
      Yup.object({
        _id: Yup.string().required(),
        name: Yup.string().required(),
      }),
    ),
    category: Yup.array()
      .of(
        Yup.object({
          _id: Yup.string().required(),
          name: Yup.string().required(),
        }),
      )
      .min(1, 'One or more categories are required'),
    subcategory: Yup.array().of(
      Yup.object({
        _id: Yup.string().required(),
        name: Yup.string().required(),
      }),
    ),
    isCombo: Yup.boolean(),
    comboItems: Yup.array(),
    isVariant: Yup.boolean(),
    lowStockAlert: Yup.boolean().required(),
    lowStockQuantity: Yup.number()
      .min(0)
      .when('lowStockAlert', (lowStockAlert, schema) => {
        return lowStockAlert[0] ? schema.required() : schema.optional()
      }),
    weight: Yup.number().min(0).optional(),
    instantDeliveryEnabled: Yup.boolean().required(),
    isMarkupEnabled: Yup.boolean().required(),
    markupValue: Yup.number()
      .min(0)
      .max(50)
      .when('isMarkupEnabled', (isMarkupEnabled, schema) => {
        return isMarkupEnabled[0] ? schema.required() : schema.optional()
      }),
    deliveryLocation: Yup.array()
      .of(Yup.object())
      .min(1, 'One or more delivery locations are required'),
    bundledProducts:
      productType === 'bundle'
        ? Yup.array()
            .of(Yup.object())
            .min(1, 'One or more bundle items are required')
            .required('One or more bundle items are required')
        : Yup.array().of(Yup.object()).min(0).optional(),
    trackExpiry: Yup.boolean().required(),
    expiryReminderInterval: Yup.string().when(
      'trackExpiry',
      (trackExpiry, schema) => {
        return trackExpiry[0] ? schema.required('Required') : schema.optional()
      },
    ),
    bestBeforeDate: Yup.string().when('trackExpiry', (trackExpiry, schema) => {
      return trackExpiry[0]
        ? schema
            .required('Best before date is required')
            .test(
              'is-not-past',
              'Start date cannot be in the past',
              function (value) {
                return (
                  !value ||
                  new Date(value).setHours(0, 0, 0, 0) >=
                    new Date().setHours(0, 0, 0, 0)
                )
              },
            )
        : schema.optional()
    }),
  })
}

export const CreateSubVariantSchema = Yup.object().shape({
  value: Yup.string().required('Required'),
  vendor: Yup.object({
    _id: Yup.string().required('Required'),
    name: Yup.string().required('Required'),
  }).required('Required'),
})

export const AddDiscountSchema = Yup.object().shape({
  minimumAmount: Yup.string()
    .test('is-valid-amount', `Invalid Amount`, function (value) {
      return parseCurrencyToNumber(value) !== null
    })
    .required('Required'),
  maximumAmount: Yup.string()
    .test('is-valid-amount', `Invalid Amount`, function (value) {
      return parseCurrencyToNumber(value) !== null
    })
    .required('Required'),
  type: Yup.string()
    .oneOf(['items', 'delivery', 'cart'], 'Invalid discount type')
    .required('Required'),
  status: Yup.string()
    .oneOf(['inactive', 'active'], 'Invalid status')
    .required('Required'),
  valueType: Yup.string()
    .when('type', {
      is: (type: string) => type !== 'payment_method',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired(),
    })
    .oneOf(['percent', 'constant']),
  value: Yup.string().when('type', {
    is: (type: string) => type !== 'payment_method',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  code: Yup.string().when('type', {
    is: (type: string) => type !== 'payment_method',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  usageLimit: Yup.number()
    .min(0)
    .when('type', {
      is: (type: string) => type !== 'payment_method',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired(),
    })
    .min(1),
  userUsageLimit: Yup.number()
    .min(0)
    .when('type', {
      is: (type: string) => type !== 'payment_method',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired(),
    })
    .min(1),
  autoApply: Yup.boolean(),
})

export const AddWalletAmountSchema = Yup.object().shape({
  amount: Yup.string()
    .test('is-valid-amount', `Invalid Amount`, function (value) {
      return parseCurrencyToNumber(value) !== null
    })
    .required('Required'),
})

export const RetrySettlementSchema = Yup.object().shape({
  password: Yup.string().required('Required'),
})

export const ApproveRefundSchema = Yup.object().shape({
  password: Yup.string().required('Required'),
})

export const DeclineRefundSchema = Yup.object().shape({
  reason: Yup.string().required('Required'),
})

export const ExportReportSchema = Yup.object().shape({
  startDate: Yup.date().when(
    'isCustomDateRange',
    (isCustomDateRange, schema) => {
      return isCustomDateRange[0] ? schema.required() : schema.optional()
    },
  ),
  endDate: Yup.date().when('isCustomDateRange', (isCustomDateRange, schema) => {
    return isCustomDateRange[0] ? schema.required() : schema.optional()
  }),
  isCustomDateRange: Yup.boolean().required('Required'),
  filter: Yup.string().when(
    'isCustomDateRange',
    (isCustomDateRange, schema) => {
      return isCustomDateRange[0]
        ? schema.optional()
        : schema.required('Required')
    },
  ),
})

export const AddCreatorSchema = Yup.object().shape({
  logo: Yup.string(),
  banner: Yup.string(),
  businessDescription: Yup.string().required('Business bio is required'),
  extendedBio: Yup.string(),
  businessEmail: Yup.string().email().required('Business email is required'),
  accountNumber: Yup.string().required('Required'),
  businessName: Yup.string()
    .matches(/^[A-Za-z0-9 ]+$/, 'No special characters allowed')
    .required('Business name is required'),
  businessStructure: Yup.string().required('Business structure is required'),
  businessAddress: Yup.object().required('Business address is required'),
  businessPhonenumber: Yup.string()
    .test('is-valid-phone', `Enter a valid phone number`, (value) => {
      const parsed = parsePhoneNumber(value || '')?.country ?? 'NG'
      return isPhoneNumberValid(value || '', parsed)
    })
    .required('Business phone number is required'),
  isSplitEnabled: Yup.boolean().required('Required'),
  splitPercentage: Yup.number().when(
    'isSplitEnabled',
    (isSplitEnabled, schema) => {
      return isSplitEnabled[0] ? schema.required() : schema.optional()
    },
  ),
})

export const InitiateRefundSchema = Yup.object().shape({
  images: Yup.array()
    .of(Yup.string().required('Required'))
    .min(1, 'Upload at least one proof')
    .required('Upload proof'),
  reason: Yup.string().required('Required'),
})

export const VendorServiceChargeSchema = Yup.object().shape({
  type: Yup.string().required('Required'),
  value: Yup.string().when('type', ([type], schema) => {
    if (type === 'Flat') {
      return schema
        .required(`Amount is required`)
        .test('is-valid-amount', `Invalid ${type}`, function (value) {
          return parseCurrencyToNumber(value) !== null
        })
        .test(
          'is-greater-than-zero',
          `${type} must be greater than 0`,
          function (value) {
            return (
              parseCurrencyToNumber(value) !== null &&
              parseCurrencyToNumber(value)! > 0
            )
          },
        )
    } else if (type === 'Percentage') {
      return schema
        .required(`${type} is required`)
        .test('is-valid-amount', `Invalid ${type}`, function (value) {
          return parseCurrencyToNumber(value) !== null
        })
        .test(
          'is-greater-than-zero',
          `${type} must be greater than 0`,
          function (value) {
            return (
              parseCurrencyToNumber(value) !== null &&
              parseCurrencyToNumber(value)! >= 0
            )
          },
        )
        .test(
          'is-not-more-than-hundred',
          `${type} cannot exceed 100%`,
          function (value) {
            return (
              parseCurrencyToNumber(value) !== null &&
              parseCurrencyToNumber(value)! <= 100
            )
          },
        )
    } else {
      return schema.notRequired()
    }
  }),
  minServiceCharge: Yup.string().test(
    'is-valid-amount',
    `Invalid Amount`,
    function (value) {
      return parseCurrencyToNumber(value) !== null
    },
  ),
  maxServiceCharge: Yup.string().test(
    'is-valid-amount',
    `Invalid Amount`,
    function (value) {
      return parseCurrencyToNumber(value) !== null
    },
  ),
})

export const CreateHomeSectionSchema = Yup.object().shape({
  title: Yup.string().required('Required'),
  description: Yup.string().optional(),
  sectionType: Yup.string().required('Required'),
  status: Yup.string().required('Required'),
  contentTypes: Yup.array()
    .of(Yup.string().required('Required'))
    .min(1, 'Select at least one content')
    .required('Required'),
})

export const AddExploreSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  image: Yup.string().required('Image is required'),
  linkUrl: Yup.string().url('Enter a valid url').required('URL is required'),
  description: Yup.string().required('Description is required'),
})

export const POSSchema = Yup.object().shape({
  customerEmail: Yup.string()
    .email('Enter a valid email')
    .required('Email is required'),
  customerName: Yup.string(),
  customerId: Yup.string(),
  customerPhone: Yup.string().test(
    'is-valid-phone',
    `Enter a valid phone number`,
    (value) => {
      const parsed = parsePhoneNumber(value || '')?.country ?? 'NG'
      return value ? isPhoneNumberValid(value || '', parsed) : true
    },
  ),
  fulfilmentType: Yup.string().required('Required'),
  paymentMethod: Yup.string().required('Required'),
  orderNote: Yup.string(),
})

export const AddAffiliateSchema = Yup.object().shape({
  customer: Yup.object().shape({
    id: Yup.string().required('Choose an affiliate'),
    name: Yup.string().required('Choose an affiliate'),
  }),
  lists: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string().required('Choose a list'),
        name: Yup.string().required('Choose a list'),
      }),
    )
    .min(1, 'Link at least one good list'),
  accountNumber: Yup.string()
    .optional()
    .min(10, 'Invalid account number')
    .max(10, 'Invalid account number'),
  accountName: Yup.string().when('accountNumber', (accountNumber, schema) => {
    return (accountNumber[0]?.length ?? 0) > 0
      ? schema.required('Account name is required')
      : schema.optional()
  }),
  bankCode: Yup.string().when('accountNumber', (accountNumber, schema) => {
    return (accountNumber[0]?.length ?? 0) > 0
      ? schema.required('Bank is required')
      : schema.optional()
  }),
  bankName: Yup.string().when('accountNumber', (accountNumber, schema) => {
    return (accountNumber[0]?.length ?? 0) > 0
      ? schema.required('Bank is required')
      : schema.optional()
  }),
})

export const AddAmbassadorSchema = Yup.object().shape({
  customer: Yup.object().shape({
    id: Yup.string().required('Choose an affiliate'),
    name: Yup.string().required('Choose an affiliate'),
  }),
})

export const UpdateAmbassadorSchema = Yup.object().shape({
  accountName: Yup.string().required('Account name is required'),
  accountNumber: Yup.string()
    .required('Account number is required')
    .min(10, 'Invalid account number')
    .max(10, 'Invalid account number'),
  bankCode: Yup.string().required('Bank is required'),
  bankName: Yup.string().required('Bank is required'),
})

export const BankAccountSchema = Yup.object().shape({
  accountName: Yup.string().required('Account name is required'),
  accountNumber: Yup.string().required('Account number is required'),
  bankCode: Yup.string().required('Bank is required'),
  bankName: Yup.string().required('Bank is required'),
})

export const VendorSettlementConfigurationSchema = Yup.object().shape({
  settlementSchedule: Yup.string().required('Required'),
  settlementPeriod: Yup.string().required('Required'),
  settlementEnabled: Yup.boolean().required('Required'),
  settlementType: Yup.string().required('Required'),
  settlementDelayDays: Yup.string().when(
    'settlementSchedule',
    (settlementSchedule, schema) => {
      return settlementSchedule[0] === 'TIME_BASED'
        ? schema
            .required('Required')
            .test('is-valid-amount', `Invalid value`, function (value) {
              return parseCurrencyToNumber(value) !== null
            })
        : schema.optional()
    },
  ),
})

export const VendorSplitConfigurationSchema = Yup.object().shape({
  isSplitEnabled: Yup.boolean().required('Required'),
  splitPercentage: Yup.string().when(
    'isSplitEnabled',
    (isSplitEnabled, schema) => {
      return isSplitEnabled[0] ? schema.required() : schema.optional()
    },
  ),
})
